import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type PropsWithChildren,
} from "react";
import {
  useGetReportUserConfig,
  type UserReportConfig,
} from "@hooks/use-get/report-user-config";

interface ReportUserConfigContextValue {
  /** All user configs fetched in a single request. */
  allUserConfigs: UserReportConfig[];
  /** True while the initial (or invalidated) fetch is in flight. */
  loading: boolean;
  /**
   * O(n) lookup — no API call.
   * Returns the stored config for a given report, or null if none exists yet.
   */
  getConfigForReport: (reportId: string) => UserReportConfig | null;
  /**
   * Call after any mutation (save layout, save stat card, save chart, save settings).
   * Re-fetches all configs and updates the cache.
   */
  invalidate: () => Promise<void>;
}

const ReportUserConfigContext = createContext<ReportUserConfigContextValue>({
  allUserConfigs: [],
  loading: true,
  getConfigForReport: () => null,
  invalidate: async () => {},
});

export const ReportUserConfigProvider = ({ children }: PropsWithChildren) => {
  const [allUserConfigs, setAllUserConfigs] = useState<UserReportConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const { getAllUserConfigs } = useGetReportUserConfig();

  // Keep a ref to the latest getAllUserConfigs so the mount effect and invalidate
  // never re-run due to unstable function references from the underlying hook.
  const getAllUserConfigsRef = useRef(getAllUserConfigs);
  getAllUserConfigsRef.current = getAllUserConfigs;

  const runFetch = useCallback(async () => {
    setLoading(true);
    const configs = await getAllUserConfigsRef.current();
    setAllUserConfigs(configs);
    setLoading(false);
  }, []); // stable — reads latest fn via ref, no deps needed

  // Fetch once on mount.
  useEffect(() => {
    runFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getConfigForReport = useCallback(
    (reportId: string): UserReportConfig | null =>
      allUserConfigs.find(
        (c) => String(c.report_id || c.id) === String(reportId),
      ) ?? null,
    [allUserConfigs],
  );

  const invalidate = useCallback(async () => {
    await runFetch();
  }, [runFetch]);

  return (
    <ReportUserConfigContext.Provider
      value={{ allUserConfigs, loading, getConfigForReport, invalidate }}
    >
      {children}
    </ReportUserConfigContext.Provider>
  );
};

export const useReportUserConfigContext = () =>
  useContext(ReportUserConfigContext);
